import { Box, Flex, Text } from '@mantine/core'
import { useSuspenseQueries } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'

import { coinMarketQueryOptions, TrendingCardType } from '@/services/coin/market'
import type { CoinSlug, GlobalCoin, LiveCoinMarket } from '@/types/coin'

import BuzzingX from './_assets/buzzing.webp'
import HotTokens from './_assets/hot-tokens.webp'
import NewlyListed from './_assets/newly-listed.webp'
import TopMovers from './_assets/top-movers.webp'
import styles from './styles.module.css'
import TrendingCoin from './TrendingCoin'
import useDisplayCoins from './useDisplayCoins'

type TrendingCardProps = {
  type: TrendingCardType
  data?: Array<{ name: string }>
  liveCache: Record<CoinSlug, LiveCoinMarket>
}

export default function TrendingCard({ type, data, liveCache }: TrendingCardProps) {
  const t = useTranslations('trending')
  const titleMap = {
    [TrendingCardType.HotTokens]: {
      title: t('hotTokens'),
      subtitle: t('topTrendingTokens'),
      bg: HotTokens,
    },
    [TrendingCardType.BuzzingX]: {
      title: t('buzzingX'),
      subtitle: t('socialSentimentMovers'),
      bg: BuzzingX,
    },
    [TrendingCardType.TopMovers]: {
      title: t('topMovers'),
      subtitle: t('biggestDailyGainersAndLosers'),
      bg: TopMovers,
    },
    [TrendingCardType.NewlyListed]: {
      title: t('newlyListed'),
      subtitle: t('postListingMomentum'),
      bg: NewlyListed,
    },
  }

  const candidateCoins = useDisplayCoins(data)

  // Batch fetch market data for all candidates
  const marketQueries = useSuspenseQueries({
    queries: (candidateCoins || []).map(coin => coinMarketQueryOptions(coin.slug, true)),
  })

  // Collect tradable and non-tradable coins separately
  const tradableCoins: GlobalCoin[] = []
  const nonTradableCoins: GlobalCoin[] = []

  for (let i = 0; i < marketQueries.length; i++) {
    const marketData = marketQueries[i].data
    const coin = candidateCoins![i]

    if (marketData && (marketData.app_tradable || marketData.exchange_tradable)) {
      tradableCoins.push(coin)
    } else if (marketData) {
      nonTradableCoins.push(coin)
    }
  }

  // Prioritize tradable coins, then fill with non-tradable ones, total 3 coins
  const displayCoins = [...tradableCoins, ...nonTradableCoins].slice(0, 3)

  if (displayCoins.length === 0) return null

  return (
    <Box className={styles.trendingCard}>
      {/* Image Container */}
      <Flex
        gap={4}
        className={styles.imageContainer}
        style={{
          background: `linear-gradient(180deg, rgba(0, 0, 0, 0.00) -21.51%, #151D32 100%), linear-gradient(270deg, rgba(0, 0, 0, 0.00) -4.49%, rgba(21, 29, 50, 0.70) 74.88%), url(${titleMap[type].bg.src}) 50% / cover no-repeat`,
        }}
      >
        <Text variant="body-emphasis" className={styles.trendingTitle}>
          {titleMap[type].title}
        </Text>

        <Text variant="subtitle-paragraph" c="var(--content-secondary)">
          {titleMap[type].subtitle}
        </Text>
      </Flex>

      {/* Content */}
      <Flex className={styles.contentContainer} gap={4}>
        {displayCoins?.map(coin => (
          <TrendingCoin key={coin.id} {...coin} livePrice={liveCache[coin.slug]?.usd_price} />
        ))}
      </Flex>
    </Box>
  )
}
